import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, FileText, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { LeftDot, RightDot } from "../../../shared/action-core";
import { CardHeader, FieldLabel } from "./card-handler";
import { DeleteModal } from "./delete-action";
import useRequest from "../../../hook/use-req";
import useRcsBotReq from "../../../hook/rcs-bot-req";
import { useTenantId } from "../../../hook/use-tenant-id";
import type { TemplateModule } from "../../../../views/marketing-pages/templates/types";
import { RcsTemplateCreator } from "./rcs-template-creator";

interface RcsActionCardProps {
  node: ActionNode;
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  // true when rendered inside RcsConfigPanel instead of on the canvas.
  bare?: boolean;
}

const CardShell: React.FC<{
  title: string;
  description?: string;
  onDelete: () => void;
  onReset: () => void;
  children: React.ReactNode;
  // true when rendered inside RcsConfigPanel — the panel supplies its own
  // header/border/canvas-connector chrome, so this just renders the fields.
  bare?: boolean;
}> = ({ title, description, onDelete, onReset, children, bare }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  if (bare) return <>{children}</>;

  return (
    <>
      <div className="relative bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 w-[270px]">
        <LeftDot />
        <RightDot />
        <CardHeader title={title} description={description} onDelete={() => setShowDeleteModal(true)} onReset={onReset} />
        {children}
      </div>
      {showDeleteModal && (
        <DeleteModal
          title="Delete action?"
          description="This action will be removed from the flow and won't run when the trigger is activated."
          confirmLabel="Delete Action"
          onConfirm={() => { onDelete(); setShowDeleteModal(false); }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};

const TextArea: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string }> = ({
  value,
  onChange,
  placeholder,
}) => (
  <textarea
    rows={3}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300 resize-none mb-3"
  />
);

const BranchIndicators: React.FC<{ trueLabel: string; falseLabel: string }> = ({ trueLabel, falseLabel }) => (
  <div className="mt-3 space-y-2">
    <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm">
      <span className="font-medium text-gray-700 dark:text-gray-300">{trueLabel}</span>
      <div className="w-3 h-3 rounded-full bg-gray-400 border-2 border-white shadow" />
    </div>
    <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm">
      <span className="font-medium text-gray-700 dark:text-gray-300">{falseLabel}</span>
      <div className="w-3 h-3 rounded-full bg-gray-400 border-2 border-white shadow" />
    </div>
  </div>
);

// ── Send RCS Template ─────────────────────────────────────────────────────────
// Same picker UX as every other "pick a template" card in this app: a
// dropdown of the tenant's existing RCS templates, searchable, with a
// "Create new template" escape hatch (RcsTemplateCreator) and a manual-ID
// fallback. Ported as-is from the old RCS Bot Studio's RcsTemplateField.
export const SendRcsTemplateCard: React.FC<RcsActionCardProps> = ({ node, onUpdate, onDelete, onReset, bare }) => {
  const templateId: string = node.config?.template_id ?? "";
  const token = localStorage.getItem("authToken");
  const { makeRequest: getTemplates, loading } = useRequest("/templates/", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [templates, setTemplates] = useState<TemplateModule[]>([]);
  const [manualMode, setManualMode] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [tenantAgentId, setTenantAgentId] = useState("");
  const tenantIdForAgent = useTenantId();
  const { makeRequest: getTenantRcsAgent } = useRcsBotReq("/rcs/agent", "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    if (!tenantIdForAgent) return;
    getTenantRcsAgent(undefined, { tenantId: tenantIdForAgent }).then(([a, status]) => {
      if (status < 400) setTenantAgentId(a?.agent_id || "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantIdForAgent]);

  const reloadTemplates = async () => {
    try {
      const [response] = await getTemplates(undefined, { page: 1, limit: 500 });
      const rows: TemplateModule[] = Array.isArray(response)
        ? response : Array.isArray((response as any)?.data) ? (response as any).data : [];
      setTemplates(rows.filter((t) => (t.channel || "").toLowerCase() === "rcs"));
    } catch { /* silent */ }
  };

  // Fetch RCS templates when the dropdown opens (cached after first load).
  useEffect(() => {
    if (!open || templates.length > 0) return;
    (async () => {
      try {
        const [response] = await getTemplates(undefined, { page: 1, limit: 500 });
        const rows: TemplateModule[] = Array.isArray(response)
          ? response
          : Array.isArray((response as any)?.data)
            ? (response as any).data
            : [];
        setTemplates(rows.filter((t) => (t.channel || "").toLowerCase() === "rcs"));
      } catch (err) {
        toast.error(`Could not load templates: ${(err as Error).message}`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      (t.template_name || "").toLowerCase().includes(q) ||
      (t.content || "").toLowerCase().includes(q),
    );
  }, [templates, search]);

  const selected = templates.find((t) => t.template_name === templateId);

  const pickTemplate = (t: TemplateModule) => {
    onUpdate({
      ...node.config,
      template_id: t.template_name,
      template_variables: Object.fromEntries(
        (t.variables || []).map((v: any) => [v.name, v.example || `{{${v.name}}}`]),
      ),
      replyText:
        t.content ||
        (Array.isArray((t as any).components)
          ? ((t as any).components.find((c: any) => c?.type === "text")?.text ?? "")
          : ""),
      template_preview_image: extractTemplatePreviewImage(t),
    });
    setOpen(false);
    setSearch("");
  };

  return (
    <CardShell title="Send RCS Template" onDelete={onDelete} onReset={onReset} bare={bare}>
      <FieldLabel>RCS Template</FieldLabel>
      <div className="relative mb-2">
        <button
          onClick={() => setOpen((s) => !s)}
          className="w-full flex items-center justify-between gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 hover:border-indigo-300"
        >
          <span className="flex items-center gap-2 min-w-0">
            <FileText size={14} className={templateId ? "text-indigo-600" : "text-[#7B7B7B] font-semibold dark:text-gray-500"} />
            <span className={`truncate ${templateId ? "text-gray-900 dark:text-gray-100" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}>
              {templateId || "Choose an RCS template…"}
            </span>
          </span>
          <ChevronDown size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
        </button>

        {open && (
          <div className="absolute z-20 mt-1 left-0 right-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-80 flex flex-col">
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 px-2 py-1.5">
              <Search size={12} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search RCS templates…"
                className="flex-1 text-xs outline-none placeholder-gray-400"
              />
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              <button
                onClick={() => setCreatorOpen(true)}
                className="sticky top-0 z-10 w-full flex items-center gap-1.5 px-3 py-2 text-xs text-indigo-700 bg-indigo-50 hover:bg-indigo-100 font-semibold border-b border-indigo-100"
              >
                <Plus size={12} /> Create new template
              </button>
              {loading ? (
                <p className="text-center text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-4">Loading…</p>
              ) : filtered.length === 0 ? (
                <p className="text-center text-xs text-gray-500 dark:text-gray-400 py-4">No RCS templates found.</p>
              ) : (
                filtered.map((t) => {
                  const isSelected = t.template_name === templateId;
                  return (
                    <button
                      key={t.id}
                      onClick={() => pickTemplate(t)}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-start gap-2 ${
                        isSelected ? "bg-indigo-50" : ""
                      }`}
                    >
                      {isSelected ? (
                        <CheckCircle2 size={13} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <FileText size={13} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">{t.template_name}</p>
                        <p className="text-[10px] uppercase tracking-wider text-[#7B7B7B] font-semibold dark:text-gray-500 font-mono">
                          {t.category || "—"} · {t.language || "en"}
                          {t.is_global && (
                            <span className="ml-1 text-emerald-600 font-semibold">GLOBAL</span>
                          )}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {selected && (
        <div className="mb-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">Preview</p>
          <p className="text-xs text-gray-700 dark:text-gray-300 mt-0.5 whitespace-pre-wrap line-clamp-3">
            {selected.content ||
              (Array.isArray((selected as any).components)
                ? ((selected as any).components.find((c: any) => c?.type === "text")?.text ?? "(media-only template)")
                : "(no preview)")}
          </p>
        </div>
      )}

      <button
        onClick={() => setManualMode((s) => !s)}
        className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
      >
        {manualMode ? "Hide" : "Type template ID manually"}
      </button>
      {manualMode && (
        <input
          type="text"
          value={templateId}
          onChange={(e) => onUpdate({ ...node.config, template_id: e.target.value })}
          placeholder="template_name"
          className="mt-1 w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs font-mono"
        />
      )}

      <RcsTemplateCreator
        open={creatorOpen}
        onClose={() => setCreatorOpen(false)}
        agentId={tenantAgentId}
        onCreated={async (tpl) => {
          await reloadTemplates();
          pickTemplate(tpl);
        }}
      />
    </CardShell>
  );
};

// ── Send Menu (quick-reply chips) ─────────────────────────────────────────────
export const SendMenuCard: React.FC<RcsActionCardProps> = ({ node, onUpdate, onDelete, onReset, bare }) => {
  const chips: { text: string; postbackData: string }[] = node.config?.chips || [];

  const updateChip = (i: number, patch: Partial<{ text: string; postbackData: string }>) => {
    const next = chips.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    onUpdate({ ...node.config, chips: next });
  };
  const addChip = () => onUpdate({ ...node.config, chips: [...chips, { text: "", postbackData: "" }] });
  const removeChip = (i: number) => onUpdate({ ...node.config, chips: chips.filter((_, idx) => idx !== i) });

  return (
    <CardShell title="Send Menu" onDelete={onDelete} onReset={onReset} bare={bare}>
      <FieldLabel>Reply Message</FieldLabel>
      <TextArea
        value={node.config?.replyText ?? ""}
        onChange={(v) => onUpdate({ ...node.config, replyText: v })}
        placeholder="What the customer reads"
      />
      <FieldLabel>Chips</FieldLabel>
      <div className="space-y-2">
        {chips.map((chip, i) => (
          <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <input
                value={chip.text}
                onChange={(e) => updateChip(i, { text: e.target.value })}
                placeholder="Chip label"
                className="flex-1 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-300"
              />
              <button onClick={() => removeChip(i)} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500 shrink-0">
                <X size={14} />
              </button>
            </div>
            <input
              value={chip.postbackData}
              onChange={(e) => updateChip(i, { postbackData: e.target.value })}
              placeholder="Postback data"
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-300"
            />
          </div>
        ))}
        <button
          onClick={addChip}
          className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
        >
          <Plus size={12} /> Add chip
        </button>
      </div>
    </CardShell>
  );
};

// ── Send Carousel ─────────────────────────────────────────────────────────────
export const SendCarouselCard: React.FC<RcsActionCardProps> = ({ node, onUpdate, onDelete, onReset, bare }) => {
  const cards: { title: string; description?: string; imageUrl?: string }[] = node.config?.cards || [];

  const updateCard = (i: number, patch: Partial<{ title: string; description: string; imageUrl: string }>) => {
    const next = cards.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    onUpdate({ ...node.config, cards: next });
  };
  const addCard = () => onUpdate({ ...node.config, cards: [...cards, { title: "", description: "", imageUrl: "" }] });
  const removeCard = (i: number) => onUpdate({ ...node.config, cards: cards.filter((_, idx) => idx !== i) });

  return (
    <CardShell title="Send Carousel" onDelete={onDelete} onReset={onReset} bare={bare}>
      <FieldLabel>Reply Message</FieldLabel>
      <TextArea
        value={node.config?.replyText ?? ""}
        onChange={(v) => onUpdate({ ...node.config, replyText: v })}
        placeholder="What the customer reads"
      />
      <FieldLabel>Cards</FieldLabel>
      <div className="space-y-2">
        {cards.map((card, i) => (
          <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <input
                value={card.title}
                onChange={(e) => updateCard(i, { title: e.target.value })}
                placeholder="Card title"
                className="flex-1 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-300"
              />
              <button onClick={() => removeCard(i)} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500 shrink-0">
                <X size={14} />
              </button>
            </div>
            <input
              value={card.description ?? ""}
              onChange={(e) => updateCard(i, { description: e.target.value })}
              placeholder="Description"
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-300"
            />
            <input
              value={card.imageUrl ?? ""}
              onChange={(e) => updateCard(i, { imageUrl: e.target.value })}
              placeholder="Image URL"
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-300"
            />
          </div>
        ))}
        <button
          onClick={addCard}
          className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
        >
          <Plus size={12} /> Add card
        </button>
      </div>
    </CardShell>
  );
};

// ── Send Text ──────────────────────────────────────────────────────────────────
export const SendTextCard: React.FC<RcsActionCardProps> = ({ node, onUpdate, onDelete, onReset, bare }) => (
  <CardShell title="Send Text" onDelete={onDelete} onReset={onReset} bare={bare}>
    <FieldLabel>Reply Message</FieldLabel>
    <TextArea
      value={node.config?.replyText ?? ""}
      onChange={(v) => onUpdate({ ...node.config, replyText: v })}
      placeholder="What the customer reads"
    />
  </CardShell>
);

// ── Wait for Reply ─────────────────────────────────────────────────────────────
// Branching only — routing (Default vs Chip Reply) is wired the same way
// Condition's true_next/false_next is: drop the next action right after this
// one to fill the open branch (see create-builder/index.tsx's handleDropAction).
export const WaitForReplyCard: React.FC<RcsActionCardProps> = ({ node, onDelete, onReset, bare }) => (
  <CardShell
    title="Wait for Reply"
    description="Pauses until the customer taps a chip or replies with text."
    onDelete={onDelete}
    onReset={onReset}
    bare={bare}
  >
    <BranchIndicators trueLabel="Chip Reply" falseLabel="Default" />
  </CardShell>
);

// ── Set Variable ───────────────────────────────────────────────────────────────
export const SetVariableCard: React.FC<RcsActionCardProps> = ({ node, onUpdate, onDelete, onReset, bare }) => (
  <CardShell title="Set Variable" onDelete={onDelete} onReset={onReset} bare={bare}>
    <FieldLabel>Variable name</FieldLabel>
    <input
      value={node.config?.variable ?? ""}
      onChange={(e) => onUpdate({ ...node.config, variable: e.target.value })}
      placeholder="plan_sku"
      className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300 mb-3"
    />
    <FieldLabel>Value</FieldLabel>
    <input
      value={node.config?.value ?? ""}
      onChange={(e) => onUpdate({ ...node.config, value: e.target.value })}
      placeholder="M2"
      className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300"
    />
  </CardShell>
);

// ── Escalate to Human ─────────────────────────────────────────────────────────
export const EscalateToHumanCard: React.FC<RcsActionCardProps> = ({ node, onUpdate, onDelete, onReset, bare }) => (
  <CardShell title="Escalate to Human" onDelete={onDelete} onReset={onReset} bare={bare}>
    <FieldLabel>Reason for transfer</FieldLabel>
    <input
      value={node.config?.transfer_reason ?? ""}
      onChange={(e) => onUpdate({ ...node.config, transfer_reason: e.target.value })}
      placeholder="e.g. user_requested_support"
      className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300"
    />
  </CardShell>
);

// ── Confirm Order ──────────────────────────────────────────────────────────────
export const ConfirmOrderCard: React.FC<RcsActionCardProps> = ({ node, onUpdate, onDelete, onReset, bare }) => (
  <CardShell title="Confirm Order" description="Terminal step — writes the order and sends a confirmation." onDelete={onDelete} onReset={onReset} bare={bare}>
    <FieldLabel>Confirmation Message</FieldLabel>
    <TextArea
      value={node.config?.replyText ?? ""}
      onChange={(v) => onUpdate({ ...node.config, replyText: v })}
      placeholder="Thanks! Your order {{order_ref}} is confirmed."
    />
  </CardShell>
);

// ── Branch (RCS variable-equals condition) ────────────────────────────────────
export const RcsBranchCard: React.FC<RcsActionCardProps> = ({ node, onUpdate, onDelete, onReset, bare }) => (
  <CardShell title="Branch" onDelete={onDelete} onReset={onReset} bare={bare}>
    <FieldLabel>Which value to check</FieldLabel>
    <input
      value={node.config?.variable ?? ""}
      onChange={(e) => onUpdate({ ...node.config, variable: e.target.value })}
      placeholder="plan_sku"
      className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300 mb-3"
    />
    <FieldLabel>Must equal</FieldLabel>
    <input
      value={node.config?.equals ?? ""}
      onChange={(e) => onUpdate({ ...node.config, equals: e.target.value })}
      placeholder="M2"
      className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300"
    />
    <BranchIndicators trueLabel="Match" falseLabel="No Match" />
  </CardShell>
);

// Best-effort thumbnail for a picked template — checked in the order an
// operator would actually see them render: an image header, then a rich
// card's image, then the first carousel card's image.
export function extractTemplatePreviewImage(t: TemplateModule): string {
  if (t.header_type === "image" && (t as any).header_content) return (t as any).header_content;
  const components = (t as any).components as any[] | undefined;
  if (!Array.isArray(components)) return "";
  const richCard = components.find((c) => c?.type === "rich_card" && c?.media_url);
  if (richCard) return richCard.media_url;
  const carousel = components.find((c) => c?.type === "carousel" && Array.isArray(c?.cards));
  const firstCardWithImage = carousel?.cards?.find((c: any) => c?.media_url);
  return firstCardWithImage?.media_url || "";
}
